import { useEffect } from 'react';
import { useQuery } from '@apollo/client';

import OrderSummaryPage from '../../components/payments/OrderSummaryPage';
import EnhancementPackageDetailsDisplay from '../../components/EnhancementPackageDetailsDisplay';
import { PURCHASED_ENHANCEMENT_QUERY } from '../../queries';
import Button from '../../components/inputs/Button';
import ErrorText from '../../components/ErrorText';

function OrderLabel({ order }) {
  const filename = `${order.seedFileName ? order.seedFileName : order.audienceName}`;
  return (
    <p className="mb-10">
      Enhancement File/Audience Name: <strong> {filename || 'N/A'} </strong>
    </p>
  );
}

function OrderDetails({ order }) {
  const { packageName } = order;

  return (
    <div className="mb-14">
      <h4 className="mb-2">Enhancement</h4>
      <EnhancementPackageDetailsDisplay packageName={packageName} />
    </div>
  );
}

function DownloadEnhancementButton({ packageId, onDownloadStarted }) {
  const { loading, error, data, startPolling, stopPolling } = useQuery(
    PURCHASED_ENHANCEMENT_QUERY,
    {
      variables: {
        packageId,
      },
      fetchPolicy: 'no-cache',
    }
  );

  // The scan runs on Athena for minutes; the server answers immediately with
  // downloadLink: null while it does, so poll for the result instead of
  // holding one request open and reading its timeout as a failure.
  //
  // startPolling rather than the `pollInterval` option — the option did not
  // take effect here, so the button never flipped to the download link on its
  // own. Re-asserted on every result rather than once on mount:
  // AuthAndApolloProvider rebuilds the ApolloClient on re-render, which hands
  // useQuery a fresh observable that a mount-only startPolling would not reach,
  // silently ending the polling. startPolling is idempotent, so re-calling it
  // just re-arms the same 3s cadence.
  useEffect(() => {
    if (error || data?.purchasedEnhancement?.downloadLink) {
      stopPolling();
    } else {
      startPolling(3000);
    }
    // `data` belongs in the deps: the sibling insights page omits it, so its
    // guard never re-runs once the link arrives and it polls forever.
  }, [error, data, startPolling, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  // downloadLink === null means the scan is still running — that is progress,
  // not an error.
  if (loading || data?.purchasedEnhancement?.downloadLink === null)
    return (
      <div className="relative">
        <Button type="button" disabled className="min-w-max animate-pulse">
          Retrieving your download...
        </Button>
        <div className="absolute mt-1 left-0 right-0">
          <p className="text-sm -ml-3 text-center text-gray-500">
            Hang tight, this can take a few minutes.
          </p>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="relative">
        <Button type="button" disabled className="min-w-max">
          Download Enhancement
        </Button>
        <div className="absolute mt-1">
          <ErrorText
            message={
              error.message.toLowerCase().includes('download pii')
                ? error.message
                : 'Failed to retrieve your download.'
            }
            className="w-64 text-sm"
          />
        </div>
      </div>
    );

  return (
    <>
      {data?.purchasedEnhancement && (
        <a
          href={data?.purchasedEnhancement?.downloadLink}
          download
          onClick={onDownloadStarted}
        >
          <Button type="button" primary className="min-w-max">
            Download Enhancement
          </Button>
        </a>
      )}
    </>
  );
}

export default function EnhancementOrderSummary() {
  return (
    <OrderSummaryPage
      OrderLabel={OrderLabel}
      OrderDetails={OrderDetails}
      DownloadButton={DownloadEnhancementButton}
    />
  );
}
